import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setAuthToken, setUnauthorizedHandler, getAuthToken, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Shared probe used on first mount AND whenever the app comes back to the
    // foreground after being backgrounded. Must be stable (useCallback) so the
    // visibilitychange listener below gets a fresh reference every render.
    const probeAuth = useCallback(async ({ silent } = {}) => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (err) {
            if (!silent) console.warn("auth probe failed", err?.response?.status || err?.message);
            // 401 means no / expired session — treat as signed out. Network
            // errors should NOT log the user out silently; keep whatever
            // state we had so a reconnect brings the app back to life.
            if (err?.response?.status === 401) setUser(false);
            return null;
        }
    }, []);

    // Initial boot probe.
    useEffect(() => {
        let mounted = true;
        (async () => {
            await probeAuth({ silent: true });
            if (mounted) setLoading(false);
        })();
        return () => { mounted = false; };
    }, [probeAuth]);

    // Centralised 401 handler for the axios interceptor — when a request
    // comes back unauthorized (token expired in background) we flip the user
    // to signed-out so the UI can react immediately instead of hanging.
    useEffect(() => {
        setUnauthorizedHandler(() => setUser(false));
        return () => setUnauthorizedHandler(null);
    }, []);

    // Re-verify auth + nudge the active page to refetch whenever the app
    // becomes visible again. This fixes the "open the app after a while and
    // it stays on the loading screen / Browse is stuck" symptom on iOS where
    // the WebView keeps the old JS state but all pending fetches were killed.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            if (!getAuthToken()) return;
            probeAuth({ silent: true });
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
        };
    }, [probeAuth]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setAuthToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        setAuthToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (err) {
            console.warn("logout request failed", err?.message);
        }
        setAuthToken(null);
        setUser(false);
    };

    const refresh = async () => probeAuth({ silent: true });

    return (
        <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh, formatApiErrorDetail }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
